import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import * as Yup from "yup";
import { Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { Notification } from "../../utils/Notification";
import API from "../../utils/Axios.utils";
import useAuth from "../../hooks/useAuth";

const AddSkill = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [coreSkills, setCoreSkills] = useState([]);
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  // New popup state
  const [openAddCoreSkill, setOpenAddCoreSkill] = useState(false);

  const handleAlert = () => {
    setAlert(false);
  };

  // Fetch core skills on mount and after adding new core skill
  const fetchCoreSkills = async () => {
    // const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
    const config = {
      headers: { Authorization: `Bearer ${token}` },
    };
    try {
      const response = await API.get("/api/core-skill/coreSkills", config);
      setCoreSkills(response.data.coreSkills || []);
    } catch (error) {
      setAlertContent("Failed to load core skills");
      setAlertType("error");
      setAlert(true);
    }
  };

  useEffect(() => {
    fetchCoreSkills();
  }, []);

  const initialValues = {
    coreSkillId: "",
    techSkills: "",
  };

  const validationSchema = Yup.object().shape({
    coreSkillId: Yup.string().required("Core Skill is required"),
    techSkills: Yup.string()
      .required("Tech Skills are required")
      .test(
        "valid-tech-skills",
        "Enter at least one tech skill, separated by commas if multiple",
        (value) => value && value.trim().length > 0
      ),
  });

  const onSubmit = async (values) => {
    try {
      const techSkillsArray = values.techSkills
        .split(",")
        .map((skill) => skill.trim())
        .filter((skill) => skill.length > 0);

      const payload = {
        coreSkillId: values.coreSkillId,
        techSkills: techSkillsArray,
      };

      // const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const res = await API.post("/api/tech-skill/addtechSkill", payload, config);
      setAlertContent(res?.data?.message || "Tech skills added successfully");
      setAlertType("success");
      setAlert(true);
      setTimeout(() => {
        navigate("/skills");
      }, 1500);
    } catch (error) {
      setAlertContent(error?.response?.data?.message || "Failed to add tech skills");
      setAlertType("error");
      setAlert(true);
    }
  };

  // Add Core Skill Formik
  const addCoreSkillInitialValues = { name: "" };

  const addCoreSkillValidationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Core Skill Name is required")
      .max(50, "Core Skill Name cannot exceed 50 characters")
      .matches(
        /^[a-zA-Z\s]+$/,
        "Core Skill Name must contain only alphabetic characters and spaces."
      ),
  });

  const handleAddCoreSkillSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // const token = JSON.parse(localStorage.getItem("userLoggedIn")).token;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const payload = { name: values.name };

      const res = await API.post("/api/core-skill/addcoreSkill", payload, config);
      setAlertContent(res?.data?.message || "Core Skill added successfully");
      setAlertType("success");
      setAlert(true);
      setOpenAddCoreSkill(false);
      resetForm();
      fetchCoreSkills(); // refresh core skills list to include newly added skill
    } catch (error) {
      setAlertContent(error?.response?.data?.message || "Failed to add core skill");
      setAlertType("error");
      setAlert(true);
    } finally {
      setSubmitting(false);
    }
  };

  const navigateToPreviousPage = () => {
    navigate(-1);
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
        Back
      </Button>
      <div
        style={{
          border: "1px solid #989898",
          borderRadius: "10px",
          padding: "15px",
        }}
      >
        <div
          style={{
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "20px",
            fontWeight: "500",
            letterSpacing: 1,
            textAlign: "left",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Create Skill</span>
          <Button variant="contained" onClick={() => setOpenAddCoreSkill(true)}>
            Add Core Skill
          </Button>
        </div>

        <div style={{ display: "grid", marginTop: 15 }}>
          <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
            {(props) => (
              <Form noValidate>
                <Grid container spacing={2}>
                  <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                    <span style={{ fontSize: "16px" }}>Core Skill</span>
                    <select
                      name="coreSkillId"
                      id="coreSkillId"
                      value={props.values.coreSkillId}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      style={{ width: "100%", padding: "8px" }}
                    >
                      <option value="">Select Core Skill</option>
                      {coreSkills.map((skill) => (
                        <option key={skill.id} value={skill.id}>
                          {skill.name}
                        </option>
                      ))}
                    </select>
                    {props.touched.coreSkillId && props.errors.coreSkillId ? (
                      <div style={{ color: "red", marginTop: "4px", fontSize: "14px" }}>{props.errors.coreSkillId}</div>
                    ) : null}
                  </Grid>

                  <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                    <span style={{ fontSize: "16px" }}>Technical Skills</span>
                    <input
                      type="text"
                      id="techSkills"
                      name="techSkills"
                      placeholder="e.g. ReactJs, Fast API, MySQL"
                      value={props.values.techSkills}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      style={{ width: "100%", padding: "8px" }}
                    />
                    {props.touched.techSkills && props.errors.techSkills ? (
                      <div style={{ color: "red", marginTop: "4px", fontSize: "14px" }}>{props.errors.techSkills}</div>
                    ) : null}
                  </Grid>

                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                      }}
                    >
                      <Button
                        variant="contained"
                        aria-haspopup="true"
                        style={{
                          background: "#2777AA",
                          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                          fontSize: "16px",
                          fontWeight: "500",
                          letterSpacing: 1,
                        }}
                        type="submit"
                        disabled={props.isSubmitting}
                      >
                        Submit
                      </Button>
                    </div>
                  </Grid>
                </Grid>

                <Notification message={alertContent} open={alert} severity={alertType} onClose={handleAlert} />
              </Form>
            )}
          </Formik>
        </div>

        {/* Add Core Skill Dialog */}
        <Dialog 
          open={openAddCoreSkill} 
          onClose={(event, reason) => {
            if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
              return;
            }
            setOpenAddCoreSkill(false);
          }}
          fullWidth
          maxWidth="xs"
          PaperProps={{ style: { minWidth: '400px', maxWidth: '400px' } }}
        >
          <DialogTitle>Add Core Skill</DialogTitle>
          <Formik
            initialValues={addCoreSkillInitialValues}
            validationSchema={addCoreSkillValidationSchema}
            onSubmit={handleAddCoreSkillSubmit}
          >
            {(props) => (
              <Form>
                <DialogContent dividers>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    name="name"
                    label="Core Skill Name"
                    type="text"
                    fullWidth
                    InputProps={{
                      maxLength: 50
                    }}
                    value={props.values.name}
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    error={props.touched.name && Boolean(props.errors.name)}
                    helperText={props.touched.name && props.errors.name}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenAddCoreSkill(false)} color="secondary">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={props.isSubmitting} color="primary">
                    Add
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
      </div>
    </div>
  );
};

export default AddSkill;
